'use client'

import { useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/shared/hooks/use-mobile'
import { AppStoreBadge, PlayStoreBadge } from '@/shared/not-found/components/store-badges'
import { APP_DOWNLOAD_URL, gatedCopy } from './app-invite.constants'
import { useAppInvite } from './app-invite-provider'
import { detectPlatform, type WebPlatform } from './platform'

/**
 * The **gated** Web → App invite modal (PB-303). Presentation only —
 * trigger/gate logic lives in `app-invite-provider`. Mount once near the app
 * root.
 *
 * Scope: this scrimmed modal is the *gated* variant only (a deliberate
 * interruption when the user attempts a web-unsupported action). The lower-intent
 * *welcome* variant is a non-blocking bottom bar (`AppInviteWelcomeBar`) so it
 * never blacks out the page the user just landed on (V1 sprint test #11).
 *
 * Two independent axes (they can disagree — a narrow desktop window, an Android
 * tablet wider than the mobile breakpoint):
 *  - CTA is chosen by OS (`detectPlatform`): desktop → QR to scan with a phone;
 *    iOS/Android web → tappable store badges (a QR is useless on the device you
 *    hold; a store link is useless on a machine that can't install the app).
 *  - Layout is chosen by viewport (`useIsMobile`): narrow → bottom sheet with a
 *    grab handle; wide → centered card with a ✕.
 * Dismiss (✕ / "Not now" / scrim tap / Esc) never navigates, so in-progress
 * input behind it is preserved.
 */
export function AppInviteModal() {
  const { active, suppressed, dismiss } = useAppInvite()
  const isMobile = useIsMobile()

  // OS is client-only; resolve after mount (SSR renders nothing since the modal
  // is closed on first paint anyway). Default 'desktop' → QR, the safe fallback.
  const [platform, setPlatform] = useState<WebPlatform>('desktop')
  useEffect(() => {
    setPlatform(detectPlatform())
  }, [])

  // Web-only surface: never render inside the native app.
  if (suppressed) return null

  // Gated variant only — the welcome variant is presented as a bottom bar.
  const open = active?.kind === 'gated'
  const copy = gatedCopy(active?.kind === 'gated' ? active.action : null)
  const showQr = platform === 'desktop'

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/80',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'
          )}
        />
        <DialogPrimitive.Content
          data-testid="app-invite-modal"
          data-variant={active?.kind}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'fixed z-50 bg-background shadow-lg duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            isMobile
              ? // Bottom sheet
                cn(
                  'inset-x-0 bottom-0 rounded-t-3xl px-6 pb-8 pt-4',
                  'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom'
                )
              : // Centered card
                cn(
                  'left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl px-8 py-8',
                  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
                )
          )}
        >
          {/* Grab handle (mobile) */}
          {isMobile && (
            <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-muted" aria-hidden="true" />
          )}

          {/* Close ✕ (desktop) */}
          {!isMobile && (
            <DialogPrimitive.Close
              className="absolute right-5 top-5 rounded-sm text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          )}

          <div className="flex flex-col items-center text-center">
            {/* App-icon tile */}
            <div className="-mt-2 mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-background shadow-md ring-1 ring-border">
              {/* Static local SVG — next/image has nothing to optimize here
                  (SVG is vector, and the optimizer refuses it outright unless
                  images.dangerouslyAllowSVG is enabled). Raw <img> is correct. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/logo.svg" alt="HubHive" className="h-9 w-9" />
            </div>

            <DialogPrimitive.Title className="text-2xl font-bold tracking-tight text-foreground">
              {copy.title}
            </DialogPrimitive.Title>

            <DialogPrimitive.Description className="mt-3 max-w-sm text-base text-muted-foreground">
              {showQr ? copy.bodyScan : copy.body}
            </DialogPrimitive.Description>

            {/* CTA keyed on OS: desktop → QR to scan; mobile web → store badges.
                On mobile we lead with the platform's own store (AC5) and keep the
                other visible (matches the mockups, harmless if tapped). */}
            <div className="mt-6 flex w-full items-center justify-center">
              {showQr ? (
                <div className="rounded-xl bg-white p-3 ring-1 ring-border">
                  <QRCodeSVG value={APP_DOWNLOAD_URL} size={200} />
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {platform === 'android' ? (
                    <>
                      <PlayStoreBadge />
                      <AppStoreBadge />
                    </>
                  ) : (
                    <>
                      <AppStoreBadge />
                      <PlayStoreBadge />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Dismiss */}
            <DialogPrimitive.Close className="mt-6 text-base font-medium text-muted-foreground transition-colors hover:text-foreground">
              Not now
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
